import { useEffect, useRef } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'

import {
  CREATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK,
  GET_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK,
  UPDATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK,
} from '../resourceLink/graphQL'
import { sanitiseValues } from '../templates/templateEditor/editorHelper'

import {
  CREATE_ACTION,
  GET_ACTION,
  GET_ACTIONS,
  GET_ACTIONS_AGGREGATE_STATS,
  UPDATE_ACTION,
} from './graphQL'

import {
  AssigneeInfo,
  convertToAssigneeInput,
} from '@/components/form-components/AssigneeSelect'
import { SelectedProject } from '@/components/shared-components/Filters/GenericProjectSelector'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'

import {
  CreatePlatformActionUiMutation,
  CreatePlatformActionUiMutationVariables,
  CreatePublicPlatformActionViaChecklistResourceLinkUiMutation,
  CreatePublicPlatformActionViaChecklistResourceLinkUiMutationVariables,
  PlatformActionStatus,
  PlatformActionUiQuery,
  PlatformActionUiQueryVariables,
  PublicPlatformActionViaChecklistResourceLinkUiQuery,
  PublicPlatformActionViaChecklistResourceLinkUiQueryVariables,
  UpdatePlatformActionUiMutation,
  UpdatePlatformActionUiMutationVariables,
  UpdatePublicPlatformActionViaChecklistResourceLinkUiMutation,
  UpdatePublicPlatformActionViaChecklistResourceLinkUiMutationVariables,
} from 'types/api.graphql'

export enum Field {
  Type = 'type',
  Title = 'title',
  Description = 'description',
  Status = 'status',
  PriorityType = 'priorityType',
  DueDate = 'dueDate',
  Project = 'project',
  Assignees = 'assignees',
  Attachments = 'attachments',
  Location = 'location',
  RequireAttachment = 'requireAttachment',
  Continue = 'continue',
}

export type FormFieldValues = {
  [Field.Type]: string
  [Field.Title]: string
  [Field.Description]: string
  [Field.Status]: PlatformActionStatus
  [Field.PriorityType]: string
  [Field.DueDate]: moment.Moment
  [Field.Attachments]: FileUploadResult[]
  [Field.Assignees]: AssigneeInfo[]
  [Field.Project]: SelectedProject
  [Field.Location]: string
  [Field.Continue]: boolean
  [Field.RequireAttachment]: boolean
}

export const useCreateAction = (
  templateResponseId?: string,
  templateResponseBlockIdentifier?: string,
  resourceLinkId?: string
) => {
  const [createMutation, { loading }] = useMutation<
    CreatePlatformActionUiMutation,
    CreatePlatformActionUiMutationVariables
  >(CREATE_ACTION)

  const [
    createPublicViaChecklistResourceLinkMutation,
    { loading: publicLoading },
  ] = useMutation<
    CreatePublicPlatformActionViaChecklistResourceLinkUiMutation,
    CreatePublicPlatformActionViaChecklistResourceLinkUiMutationVariables
  >(CREATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK)

  const createAction = async (values: Partial<FormFieldValues>) => {
    sanitiseValues(values)

    const {
      title,
      description,
      status,
      priorityType,
      dueDate,
      project,
      location,
      attachments,
      assignees,
      type,
      requireAttachment,
    } = values

    const attachmentIds = (attachments as FileUploadResult[])?.map(
      (att) => att.attachmentId
    )

    if (resourceLinkId) {
      const platformAction = await createPublicViaChecklistResourceLinkMutation(
        {
          variables: {
            input: {
              type,
              title,
              description,
              status,
              priorityType,
              dueDate: dueDate?.toISOString(),
              attachmentIds,
              resourceLinkId,
              templateResponseId,
              templateResponseBlockIdentifier,
            },
          },
        }
      )

      return platformAction?.data
        ?.createPublicPlatformActionViaChecklistResourceLink
    }

    const platformAction = await createMutation({
      variables: {
        input: {
          type,
          title,
          description,
          status,
          priorityType,
          dueDate: dueDate?.toISOString(),
          projectId: project?.id,
          locationId: location,
          attachmentIds,
          assignees: convertToAssigneeInput(assignees),
          templateResponseId,
          templateResponseBlockIdentifier,
          config: {
            requireAttachmentToClose: requireAttachment,
          },
        },
      },
    })

    return platformAction?.data?.createPlatformAction
  }

  return { createAction, loading: loading || publicLoading }
}

export const useUpdateAction = (
  templateResponseId?: string,
  resourceLinkId?: string
) => {
  const [updateMutation, { loading }] = useMutation<
    UpdatePlatformActionUiMutation,
    UpdatePlatformActionUiMutationVariables
  >(UPDATE_ACTION, {
    awaitRefetchQueries: true,
    refetchQueries: [GET_ACTIONS, GET_ACTIONS_AGGREGATE_STATS],
  })

  const [
    updatePublicViaChecklistResourceLinkMutation,
    { loading: publicLoading },
  ] = useMutation<
    UpdatePublicPlatformActionViaChecklistResourceLinkUiMutation,
    UpdatePublicPlatformActionViaChecklistResourceLinkUiMutationVariables
  >(UPDATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK)

  const updateAction = async (id: string, values: Partial<FormFieldValues>) => {
    sanitiseValues(values)

    const {
      title,
      description,
      status,
      priorityType,
      dueDate,
      project,
      attachments,
      assignees,
      type,
      location,
      requireAttachment,
    } = values

    const attachmentIds = (attachments as FileUploadResult[])?.map(
      (att) => att.attachmentId
    )

    if (resourceLinkId) {
      const platformAction = await updatePublicViaChecklistResourceLinkMutation(
        {
          variables: {
            id,
            input: {
              type,
              title,
              description,
              status,
              priorityType,
              dueDate: dueDate?.toISOString(),
              attachmentIds,
              resourceLinkId,
              templateResponseId,
            },
          },
        }
      )

      return platformAction?.data
        ?.updatePublicPlatformActionViaChecklistResourceLink
    }

    const platformAction = await updateMutation({
      variables: {
        id,
        input: {
          type,
          title,
          description,
          status,
          priorityType,
          dueDate: dueDate?.toISOString(),
          projectId: project?.id,
          locationId: location,
          attachmentIds,
          assignees: convertToAssigneeInput(assignees),
          config: {
            requireAttachmentToClose: requireAttachment,
          },
        },
      },
    })

    return platformAction?.data?.updatePlatformAction
  }

  return { updateAction, loading: loading || publicLoading }
}

export const useGetAction = (id: string, resourceLinkId?: string) => {
  const hasFetched = useRef(false)

  const [getAction, { data, loading }] = useLazyQuery<
    PlatformActionUiQuery,
    PlatformActionUiQueryVariables
  >(GET_ACTION, { variables: { id } })

  const [getPublicAction, { data: publicData, loading: publicLoading }] =
    useLazyQuery<
      PublicPlatformActionViaChecklistResourceLinkUiQuery,
      PublicPlatformActionViaChecklistResourceLinkUiQueryVariables
    >(GET_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK, {
      variables: { id, resourceLinkId },
    })

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true

    if (resourceLinkId) {
      getPublicAction()
    } else {
      getAction()
    }
  }, [resourceLinkId, getAction, getPublicAction])

  const action =
    data?.platformAction ||
    publicData?.publicPlatformActionViaChecklistResourceLink

  return { action, loading: loading || publicLoading }
}
